import {InputProps, MenuItem, TextField, TextFieldProps} from '@mui/material';
import _ from 'lodash';
import {useEffect, useMemo, useRef, useState} from 'react';

import API from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {useAppSelector} from '../../hooks/redux';

type Props = Pick<
  TextFieldProps,
  | 'size'
  | 'disabled'
  | 'fullWidth'
  | 'label'
  | 'error'
  | 'helperText'
  | 'InputProps'
  | 'SelectProps'
  | 'sx'
> & {
  value?: number | number[] | null;
  multiple?: boolean;
  nullLabel?: string;
  onChange?: (v: number | number[]) => void;
};

type SitePermission = {
  site_id: number;
  user_id: number;
  allowed: number | boolean;
  id: number;
};

export const SitesSelect = (props: Props) => {
  const propValue = props.value;

  // Internal state for multiple mode (tracks selection until dropdown closes)
  const [selected, setSelected] = useState<number[]>(
    props.multiple ? (props.value as number[]) ?? [] : []
  );

  // Sync internal selection when the parent's value changes (mirrors SingleMultiSelect)
  const prevValueRef = useRef(propValue);
  useEffect(() => {
    if (_.isEqual(prevValueRef.current, propValue)) {
      return;
    }
    prevValueRef.current = propValue;
    if (props.multiple && Array.isArray(propValue)) {
      setSelected(propValue as number[]);
    } else if (!props.multiple && propValue != null) {
      setSelected([propValue] as number[]);
    } else {
      setSelected([]);
    }
  }, [propValue, props.multiple]);

  const me = useAppSelector(({app}) => app.me);
  const allSites = useAppSelector(({assets}) => assets.sites);

  // null = not yet fetched; [] = fetched, user has no explicit restriction rows
  const [permissions, setPermissions] = useState<SitePermission[] | null>(null);

  // Reactive, race-safe fetch — re-runs if the logged-in user changes
  useEffect(() => {
    if (!me?.id) {
      return;
    }
    let active = true;
    API.get(`${apiBaseUrl}/site-permissions/user/${me.id}`)
      .then((resp) => {
        if (active) {
          setPermissions(resp.data ?? []);
        }
      })
      .catch(() => {
        if (active) {
          setPermissions([]);
        }
      });
    return () => {
      active = false;
    };
  }, [me?.id]);

  // Derive the list of sites this user may see (reactive — updates when either
  // the Redux sites list or the fetched permissions arrive)
  const allowedSites = useMemo(() => {
    if (permissions === null) {
      return [];
    }
    return allSites.filter((site) => {
      const match = permissions.find((p) => p.site_id === site.id);
      // No explicit row means allowed by default; row with allowed===0/false means denied
      return !match || match.allowed === 1 || match.allowed === true;
    });
  }, [allSites, permissions]);

  // Both data sources must be ready before we trust these computations
  const dataLoaded = permissions !== null && allSites.length > 0;
  const userHasAllAccess =
    dataLoaded && allowedSites.length === allSites.length;

  // Auto-select the first permitted site for restricted users, exactly once,
  // after data has loaded — only when there is no existing value and the
  // field is interactive (onChange present, not disabled)
  const didAutoSelect = useRef(false);
  useEffect(() => {
    if (didAutoSelect.current) {
      return;
    }
    if (!dataLoaded || userHasAllAccess) {
      return;
    }
    if (!props.onChange || props.disabled) {
      return;
    }
    if (allowedSites.length === 0) {
      return;
    }

    const hasCurrentValue = props.multiple
      ? Array.isArray(propValue) && (propValue as number[]).length > 0
      : propValue != null;

    if (hasCurrentValue) {
      return;
    }

    didAutoSelect.current = true;
    const firstId = allowedSites[0].id;
    if (props.multiple) {
      setSelected([firstId]);
      props.onChange([firstId]);
    } else {
      props.onChange(firstId);
    }
  }, [
    dataLoaded,
    userHasAllAccess,
    allowedSites,
    propValue,
    props.multiple,
    props.onChange,
    props.disabled,
  ]);

  // Always include any currently-referenced site(s) in the menu so that
  // read-only views and records being edited don't render a blank option
  const currentIds = props.multiple
    ? selected
    : propValue != null
      ? [propValue as number]
      : [];

  const extraSites = currentIds
    .filter((id) => !allowedSites.some((s) => s.id === id))
    .map((id) => allSites.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => s != null);

  const menuSites = [...allowedSites, ...extraSites];

  // Displayed value — defaulting is handled by the real auto-select above,
  // so we never silently fall back to "all" for restricted users
  const valueName = props.multiple
    ? selected.length > 0
      ? selected
      : userHasAllAccess
        ? ['all']
        : []
    : propValue != null
      ? propValue
      : userHasAllAccess
        ? 'all'
        : '';

  const handleChange: InputProps['onChange'] = (event: any) => {
    // For single mode we return value immediately
    if (!props.multiple) {
      return props.onChange?.(
        event.target.value === 'all' ? null : event.target.value
      );
    }

    // For multiple mode we update internal state and emit on dropdown close
    const isSelectAll = _.last(event.target.value) === 'all';
    setSelected(
      isSelectAll ? [] : event.target.value.filter((i: any) => i !== 'all')
    );
  };

  const handleClose = () => {
    // For multiple mode we return value only after dropdown is closed
    if (props.multiple) {
      props.onChange?.(selected);
    }
  };

  return (
    <TextField
      {...props}
      label={props.label || 'Sites'}
      value={valueName}
      select
      SelectProps={{
        multiple: props.multiple,
        onClose: handleClose,
        MenuProps: {
          sx: {maxHeight: '500px'},
        },
      }}
      onChange={handleChange}
    >
      {userHasAllAccess && (
        <MenuItem value="all">{props.nullLabel ?? 'All Sites'}</MenuItem>
      )}

      {menuSites.map((site) => (
        <MenuItem
          key={site.id}
          value={site.id}
          disabled={site.status === 'inactive'}
        >
          {site.name}
        </MenuItem>
      ))}
    </TextField>
  );
};
